import React from 'react';
import { Sparkles, FileText } from 'lucide-react';
import { OutletClassification } from '../../../types';

interface RatingDetailsSectionProps {
  formData: {
    rating: number;
    classificationId: string;
    areaInSqft: string;
    hasExoticFruits: boolean;
    hasHomeDelivery: boolean;
    remarks: string;
  };
  classifications: OutletClassification[];
  dataLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onFocus: (fieldName: string) => void;
  onBlur: (fieldName: string) => void;
  getPlaceholder: (fieldName: string, defaultPlaceholder: string) => string;
  onRatingChange: (rating: number) => void;
  onToggleExoticFruits: (value: boolean) => void;
  onToggleHomeDelivery: (value: boolean) => void;
}

export const RatingDetailsSection: React.FC<RatingDetailsSectionProps> = ({
  formData,
  classifications,
  dataLoading,
  onChange,
  onFocus,
  onBlur,
  getPlaceholder,
  onRatingChange,
  onToggleExoticFruits,
  onToggleHomeDelivery
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <Sparkles className="w-5 h-5 text-yellow-600" />
        <span>Rating & Other Details</span>
      </h3>
      
      <div className="space-y-4">
        {/* Rating Widget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Outlet Rating</label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => onRatingChange(star)}
                className={`w-8 h-8 flex items-center justify-center transition-colors ${
                  star <= formData.rating 
                    ? 'text-yellow-400 hover:text-yellow-500' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
              >
                <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </button>
            ))}
            <span className="ml-3 text-sm text-gray-600">
              {formData.rating > 0 ? `${formData.rating} star${formData.rating > 1 ? 's' : ''}` : 'No rating'}
            </span>
          </div>
        </div>

        {/* Conditional Star Rating Questions */}
        {formData.rating > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <Sparkles className="w-5 h-5 text-yellow-600" />
              <h4 className="text-sm font-medium text-yellow-800">Additional Questions</h4>
              <span className="text-xs text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full">
                For rated outlets
              </span>
            </div>
            
            {/* Exotic Fruits Question */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-300">
              <div className="flex items-center space-x-3">
                <span className="text-lg">🥝</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">Exotic Fruits Available?</p>
                  <p className="text-xs text-gray-600">Avocado, Dragon Fruit, Kiwi, etc.</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => onToggleExoticFruits(false)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    !formData.hasExoticFruits
                      ? 'bg-red-100 text-red-800 border border-red-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-red-50'
                  }`}
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => onToggleExoticFruits(true)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    formData.hasExoticFruits
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-green-50'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>

            {/* Home Delivery Question */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-300">
              <div className="flex items-center space-x-3">
                <span className="text-lg">🚚</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">Home Delivery Available?</p>
                  <p className="text-xs text-gray-600">Does this outlet provide delivery service?</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => onToggleHomeDelivery(false)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    !formData.hasHomeDelivery
                      ? 'bg-red-100 text-red-800 border border-red-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-red-50'
                  }`}
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => onToggleHomeDelivery(true)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    formData.hasHomeDelivery
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-green-50'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Outlet Classification</label>
            <select
              name="classificationId"
              value={formData.classificationId}
              onChange={onChange}
              disabled={dataLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            >
              <option value="">Select Classification</option>
              {classifications.map(classification => (
                <option key={classification.id} value={classification.id}>{classification.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Area (sq ft)</label>
            <input
              type="number"
              name="areaInSqft"
              value={formData.areaInSqft}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Area in sq ft"
            />
          </div>
        </div>

        {/* Remarks Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Remarks / Observations</span>
          </label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={onChange}
            onFocus={() => onFocus('remarks')}
            onBlur={() => onBlur('remarks')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder={getPlaceholder('remarks', 'Add any remarks, observations, or notes about this outlet...')}
          />
        </div>
      </div>
    </div>
  );
};

export default RatingDetailsSection;
